// ============================================================
// useSchools Hooks
// ============================================================
import { useState, useCallback, useEffect } from 'react';
import schoolService from '@/services/schoolService';

/**
 * Hook to fetch and search all schools
 */
export function useSchools(query = '', filters = null) {
  const [schools, setSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSchools = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const authStorage = localStorage.getItem('erp_auth_token');
      let orgId = null;
      if (authStorage) {
        try {
          const { state } = JSON.parse(authStorage);
          if (state?.user?.organization_id) orgId = state.user.organization_id;
        } catch { /* ignore */ }
      }
      let response;
      if (orgId) {
        response = await schoolService.getSchools(orgId);
      } else {
        response = await schoolService.searchSchools(query, filters);
      }
      setSchools(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch schools');
      setSchools([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, filters]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  return { schools, isLoading, error, refetch: fetchSchools };
}

/**
 * Hook to fetch details for a single school
 */
export function useSchoolDetails(schoolId) {
  const [school, setSchool] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSchool = useCallback(async () => {
    if (!schoolId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await schoolService.getSchoolById(schoolId);
      setSchool(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch school details');
    } finally {
      setIsLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  return { school, isLoading, error, refetch: fetchSchool };
}

/**
 * Hook to fetch a school's dashboard summary
 */
export function useSchoolSummary(schoolId) {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!schoolId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await schoolService.getSchoolSummary(schoolId);
      setSummary(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch school summary');
    } finally {
      setIsLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, isLoading, error, refetch: fetchSummary };
}

/**
 * Hook to fetch comparison data for multiple schools
 */
export function useCompareSchools(schoolIds = [], allSchools = []) {
  const [comparisonData, setComparisonData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchComparison = useCallback(async () => {
    if (!schoolIds || schoolIds.length === 0) {
      setComparisonData([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await schoolService.compareSchools(schoolIds, allSchools);
      setComparisonData(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch comparison data');
    } finally {
      setIsLoading(false);
    }
  }, [JSON.stringify(schoolIds), allSchools]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  return { comparisonData, isLoading, error, refetch: fetchComparison };
}
